import { Fragment, type ReactNode } from "react";
import { cx } from "@wato/ui";

export interface BubbleButton {
  readonly text: string;
}

export interface WhatsAppBubbleProps {
  readonly direction: "ltr" | "rtl";
  readonly header?: string | null | undefined;
  readonly body: string;
  readonly footer?: string | null | undefined;
  readonly buttons?: readonly BubbleButton[];
  readonly testId?: string;
  /** Visual tone: "neutral" (default, unchanged) or "outgoing" (a business message in a preview). */
  readonly tone?: "neutral" | "outgoing";
}

const MARKUP = /```([\s\S]+?)```|\*([^*\n]+)\*|_([^_\n]+)_|~([^~\n]+)~/g;

/** WhatsApp markup: *bold*, _italic_, ~strike~, ```mono```. Rendered as elements, never HTML. */
export function renderWhatsAppMarkup(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let key = 0;
  for (const m of text.matchAll(MARKUP)) {
    const index = m.index ?? 0;
    if (index > last) out.push(<Fragment key={key++}>{text.slice(last, index)}</Fragment>);
    if (m[1] !== undefined) out.push(<code key={key++}>{m[1]}</code>);
    else if (m[2] !== undefined) out.push(<strong key={key++}>{m[2]}</strong>);
    else if (m[3] !== undefined) out.push(<em key={key++}>{m[3]}</em>);
    else out.push(<s key={key++}>{m[4]}</s>);
    last = index + m[0].length;
  }
  if (last < text.length) out.push(<Fragment key={key}>{text.slice(last)}</Fragment>);
  return out;
}

/** Shared WhatsApp-style bubble (template editor + flow node panel). Tail follows `direction`. */
export function WhatsAppBubble({
  direction,
  header,
  body,
  footer,
  buttons = [],
  testId = "template-bubble",
  tone = "neutral",
}: WhatsAppBubbleProps) {
  return (
    <div
      data-testid={testId}
      dir={direction}
      data-tail={direction === "rtl" ? "end" : "start"}
      className={cx(
        "flex max-w-sm flex-col gap-1 rounded-lg p-3 text-start text-sm",
        tone === "outgoing" ? "bg-success-bg text-success-fg" : "bg-surface-sunken text-fg",
      )}
    >
      {header ? <p className="font-semibold">{renderWhatsAppMarkup(header)}</p> : null}
      <p className="whitespace-pre-wrap">{renderWhatsAppMarkup(body)}</p>
      {footer ? <p className="text-xs text-fg-muted">{footer}</p> : null}
      {buttons.length > 0 ? (
        <ul className="mt-1 flex flex-col gap-1">
          {buttons.map((b, i) => (
            <li
              key={i}
              className="rounded-md border border-line-strong bg-surface px-2 py-1 text-center text-fg"
            >
              {b.text}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
